import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import { ingestionApi } from './ingestionApi';

const { Title, Text } = Typography;

const RANGE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Last 7 days' },
    { value: 'month', label: 'Last 30 days' },
    { value: 'quarter', label: 'Last 90 days' },
    { value: 'all', label: 'All time' },
];

const rangeFrom = (preset) => {
    if (preset === 'all') return undefined;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    if (preset === 'week') d.setDate(d.getDate() - 6);
    else if (preset === 'month') d.setDate(d.getDate() - 29);
    else if (preset === 'quarter') d.setDate(d.getDate() - 89);
    return d.toISOString();
};

const usd = (n) => (n == null ? '-' : n > 0 && n < 0.01 ? '<$0.01' : `$${Number(n).toFixed(2)}`);
const tokens = (n) => (!n ? '-' : n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n));
const unitsLabel = (provider) => (provider === 'meshy' ? 'credits' : 'images');

const costCell = (row) => (
    <span>
        {usd(row.costUsd)}
        {row.unpriced ? <Text type="secondary"> +{row.unpriced} unpriced</Text> : null}
    </span>
);

const lineColumns = (first) => [
    first,
    { title: 'Calls', dataIndex: 'calls', key: 'calls', align: 'right' },
    { title: 'Failed', dataIndex: 'failed', key: 'failed', align: 'right', render: (v) => (v ? <Text type="danger">{v}</Text> : '-') },
    { title: 'Input tok', key: 'in', align: 'right', render: (_, r) => tokens(r.inputTokens + r.cacheReadTokens) },
    { title: 'Output tok', dataIndex: 'outputTokens', key: 'out', align: 'right', render: tokens },
    { title: 'Units', key: 'units', align: 'right', render: (_, r) => (r.units ? `${r.units} ${unitsLabel(r.provider)}` : '-') },
    { title: 'Cost', key: 'cost', align: 'right', render: (_, r) => costCell(r) },
];

const ItemBreakdown = ({ itemId }) => {
    const [summary, setSummary] = useState(null);
    useEffect(() => {
        ingestionApi.getItemCosts(itemId).then(setSummary).catch(() => setSummary({ lines: [] }));
    }, [itemId]);
    return (
        <Table
            size="small"
            pagination={false}
            loading={!summary}
            rowKey={(r) => `${r.task}/${r.model}`}
            dataSource={summary?.lines || []}
            columns={lineColumns({ title: 'Task · model', key: 't', render: (_, r) => `${r.task} · ${r.model}` })}
        />
    );
};

/**
 * Pipeline Costs: what the pipeline's paid calls (chat models, image generation, Meshy)
 * cost over a date range, by model, task, item and day, from the usage ledger.
 */
export const IngestionCosts = () => {
    const [range, setRange] = useState('month');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        ingestionApi
            .getCosts({ from: rangeFrom(range) })
            .then(setData)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [range]);

    const totals = data?.totals || { calls: 0, costUsd: 0, unpriced: 0, items: 0 };

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Title level={3}>Pipeline costs</Title>
                <Select style={{ width: 160 }} value={range} onChange={setRange} options={RANGE_OPTIONS} />
            </Space>
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card size="small" loading={loading}>
                        <Statistic title="Total" value={totals.costUsd} precision={2} prefix="$" />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small" loading={loading}>
                        <Statistic title="Calls" value={totals.calls} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small" loading={loading}>
                        <Statistic title="Items" value={totals.items} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small" loading={loading}>
                        <Statistic
                            title="Unpriced calls"
                            value={totals.unpriced}
                            valueStyle={totals.unpriced ? { color: '#d48806' } : undefined}
                        />
                        {totals.unpriced ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Meshy credits / images without a configured price (MESHY_USD_PER_CREDIT, TEXTURE_GEN_USD_PER_IMAGE)
                            </Text>
                        ) : null}
                    </Card>
                </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card size="small" title="By model" loading={loading}>
                        <Table
                            size="small"
                            pagination={false}
                            rowKey={(r) => `${r.provider}/${r.model}`}
                            dataSource={data?.byModel || []}
                            columns={lineColumns({ title: 'Model', key: 'm', render: (_, r) => (<span><Tag>{r.provider}</Tag>{r.model}</span>) })}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card size="small" title="By task" loading={loading}>
                        <Table
                            size="small"
                            pagination={false}
                            rowKey="task"
                            dataSource={data?.byTask || []}
                            columns={lineColumns({ title: 'Task', dataIndex: 'task', key: 'task' })}
                        />
                    </Card>
                </Col>
            </Row>

            <Card size="small" title="By item" loading={loading} style={{ marginBottom: 24 }}>
                <Table
                    size="small"
                    rowKey="itemId"
                    dataSource={data?.byItem || []}
                    pagination={{ pageSize: 25, showSizeChanger: false }}
                    expandable={{ expandedRowRender: (r) => <ItemBreakdown itemId={r.itemId} /> }}
                    columns={[
                        { title: 'Item', key: 'name', render: (_, r) => <Link to={`/ingestion/${r.itemId}`}>{r.name || r.itemId}</Link> },
                        { title: 'Type', dataIndex: 'itemType', key: 'type', width: 140 },
                        { title: 'Calls', dataIndex: 'calls', key: 'calls', align: 'right', width: 90 },
                        { title: 'Last call', dataIndex: 'lastAt', key: 'last', width: 180, render: (v) => (v ? new Date(v).toLocaleString() : '-') },
                        { title: 'Cost', key: 'cost', align: 'right', width: 160, render: (_, r) => costCell(r), sorter: (a, b) => a.costUsd - b.costUsd },
                    ]}
                />
            </Card>

            <Card size="small" title="By day" loading={loading}>
                <Table
                    size="small"
                    pagination={false}
                    rowKey="day"
                    dataSource={[...(data?.byDay || [])].reverse()}
                    columns={[
                        { title: 'Day', dataIndex: 'day', key: 'day' },
                        { title: 'Calls', dataIndex: 'calls', key: 'calls', align: 'right' },
                        { title: 'Cost', dataIndex: 'costUsd', key: 'cost', align: 'right', render: usd },
                    ]}
                />
            </Card>
        </div>
    );
};
